/**
 * Price Configuration — FIXED prices per country
 *
 * NO conversion. NO exchange rates. Just exact prices per market.
 * formatPriceForCountry() returns the COMPLETE formatted string.
 *
 * Country detection is SYNCHRONOUS-FIRST:
 *   1. Manual selection (localStorage)
 *   2. Cached detection (localStorage, from prior GeoIP)
 *   3. Shopify Markets (window.Shopify.country)
 *   4. Locale parsing (broad matching)
 *   5. GeoIP (async, non-blocking, cached 24h)
 *   6. Fallback: US
 */

// ─── Fixed price table ───
export const PRICE_TABLE = {
  basePrice: {
    US: 14.99,
    ES: 12.95,
    IT: 12.95,
    MX: 263,
    CL: 13000,
    CO: 56000,
    UY: 588,
  },
  compareAtPrice: {
    US: 36,
    ES: 31.1,
    IT: 31.1,
    MX: 632,
    CL: 31200,
    CO: 134400,
    UY: 1412,
  },
}

export type CountryCode = keyof typeof PRICE_TABLE.basePrice

// ─── Currency display config ───
export const CURRENCY_CONFIG: Record<
  CountryCode,
  {
    code: string
    name: string
    prefix: string
    suffix: string
    decimals: number
    decimalSep: string
    thousandSep: string
  }
> = {
  US: {
    code: 'USD',
    name: 'Estados Unidos',
    prefix: 'US$',
    suffix: '',
    decimals: 2,
    decimalSep: '.',
    thousandSep: ',',
  },
  ES: {
    code: 'EUR',
    name: 'España',
    prefix: '',
    suffix: ' €',
    decimals: 2,
    decimalSep: ',',
    thousandSep: '.',
  },
  IT: {
    code: 'EUR',
    name: 'Italia',
    prefix: '',
    suffix: ' €',
    decimals: 2,
    decimalSep: ',',
    thousandSep: '.',
  },
  MX: {
    code: 'MXN',
    name: 'México',
    prefix: 'MX$',
    suffix: '',
    decimals: 2,
    decimalSep: '.',
    thousandSep: ',',
  },
  CL: {
    code: 'CLP',
    name: 'Chile',
    prefix: '$',
    suffix: '',
    decimals: 0,
    decimalSep: '',
    thousandSep: '.',
  },
  CO: {
    code: 'COP',
    name: 'Colombia',
    prefix: 'COP ',
    suffix: '',
    decimals: 0,
    decimalSep: '',
    thousandSep: '.',
  },
  UY: {
    code: 'UYU',
    name: 'Uruguay',
    prefix: '$U ',
    suffix: '',
    decimals: 2,
    decimalSep: ',',
    thousandSep: '.',
  },
}

const VALID_CODES = new Set(Object.keys(CURRENCY_CONFIG))

function isValidCode(code: string): code is CountryCode {
  return VALID_CODES.has(code)
}

// ─── Number formatting ───
export function formatNumber(
  value: number,
  decimals: number,
  decimalSep: string,
  thousandSep: string,
): string {
  const fixed =
    decimals > 0 ? value.toFixed(decimals) : Math.round(value).toString()
  const [intPart, decPart] = fixed.split('.')
  const digits = intPart.replace('-', '')
  const isNeg = value < 0
  let formatted = ''
  for (let i = 0; i < digits.length; i++) {
    if (i > 0 && (digits.length - i) % 3 === 0) formatted += thousandSep
    formatted += digits[i]
  }
  if (isNeg) formatted = '-' + formatted
  if (decimals > 0 && decPart) formatted += decimalSep + decPart
  return formatted
}

// ─── Main format function ───
export function formatPriceForCountry(
  usdAmount: number,
  countryCode: CountryCode,
  priceType: 'base' | 'compareAt' = 'base',
): string {
  const config = CURRENCY_CONFIG[countryCode]
  const table =
    priceType === 'base' ? PRICE_TABLE.basePrice : PRICE_TABLE.compareAtPrice
  const baseUSD = PRICE_TABLE.basePrice.US
  const ratio = usdAmount / baseUSD
  const localPrice = table[countryCode] * ratio
  const numberStr = formatNumber(
    localPrice,
    config.decimals,
    config.decimalSep,
    config.thousandSep,
  )
  return `${config.prefix}${numberStr}${config.suffix}`
}

// ─── LocalStorage keys ───
const LS_MANUAL = 'selected_country' // User manually picked
const LS_DETECTED = 'detected_country' // Cached from GeoIP/detection
const LS_DETECTED_TS = 'detected_country_ts' // Timestamp of cache
const CACHE_TTL = 24 * 60 * 60 * 1000 // 24 hours

function lsGet(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}
function lsSet(key: string, val: string): void {
  try {
    localStorage.setItem(key, val)
  } catch {}
}

// ─── Broad locale → country mapping ───
// Matches both exact (es-ES) and language-only (es) locales
const LOCALE_MAP: Record<string, CountryCode> = {
  // Exact matches (highest priority)
  'es-ES': 'ES',
  'es-MX': 'MX',
  'es-CL': 'CL',
  'es-CO': 'CO',
  'es-UY': 'UY',
  'it-IT': 'IT',
  'en-US': 'US',
}

const LANG_FALLBACK: Record<string, CountryCode> = {
  es: 'ES', // Generic Spanish → Spain
  it: 'IT', // Generic Italian → Italy
  en: 'US', // Generic English → US
}

function resolveFromLocale(): CountryCode | null {
  if (typeof navigator === 'undefined' || !navigator.language) return null
  const locale = navigator.language // e.g. "es-ES", "es", "it", "en-US"

  // Try exact match first
  if (LOCALE_MAP[locale]) return LOCALE_MAP[locale]

  // Try language-only fallback (take first 2 chars)
  const lang = locale.split('-')[0].toLowerCase()
  if (LANG_FALLBACK[lang]) return LANG_FALLBACK[lang]

  return null
}

// ─── SYNCHRONOUS country resolution ───
// Tries all instant sources. Returns null only if none available.
export function resolveCountryCodeSync(): {
  code: CountryCode
  source: string
} | null {
  // 1. Manual selection by user
  const manual = lsGet(LS_MANUAL)
  if (manual && isValidCode(manual)) {
    return { code: manual, source: 'manual' }
  }

  // 2. Cached detection (from prior GeoIP or detection)
  const cached = lsGet(LS_DETECTED)
  const cachedTs = lsGet(LS_DETECTED_TS)
  if (cached && isValidCode(cached) && cachedTs) {
    const age = Date.now() - parseInt(cachedTs, 10)
    if (age < CACHE_TTL) {
      return { code: cached, source: 'cache' }
    }
  }

  // 3. Shopify Markets (available instantly in Shopify runtime)
  if (typeof window !== 'undefined') {
    const shopifyCountry = (window as any).Shopify?.country as
      | string
      | undefined
    if (shopifyCountry && isValidCode(shopifyCountry)) {
      return { code: shopifyCountry, source: 'shopify' }
    }
  }

  // 4. Locale parsing (broad matching)
  const fromLocale = resolveFromLocale()
  if (fromLocale) {
    return { code: fromLocale, source: 'locale' }
  }

  return null
}

// ─── ASYNC country resolution (GeoIP fallback) ───
// Only called when sync resolution fails. Caches result for 24h.
export async function resolveCountryCodeAsync(): Promise<{
  code: CountryCode
  source: string
}> {
  try {
    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), 3000) // 3s timeout

    const res = await fetch('https://ipapi.co/json/', {
      signal: controller.signal,
    })
    clearTimeout(timeout)

    if (res.ok) {
      const data = await res.json()
      const cc = data.country_code as string
      if (isValidCode(cc)) {
        // Cache for 24h
        lsSet(LS_DETECTED, cc)
        lsSet(LS_DETECTED_TS, Date.now().toString())
        return { code: cc, source: 'geoip' }
      }
    }
  } catch {
    // GeoIP failed — that's fine, use fallback
  }

  return { code: 'US', source: 'fallback' }
}

// ─── Save manual selection ───
export function saveManualCountry(countryCode: CountryCode): void {
  lsSet(LS_MANUAL, countryCode)
}

// ─── Clear manual selection (revert to auto-detection) ───
export function clearManualCountry(): void {
  try {
    localStorage.removeItem(LS_MANUAL)
  } catch {}
}
